import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-modern-calendar-datepicker';
import {  makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  convertDateToModernCalendarFormat,
  formatDateString,
  addMonthsToDate
} from '../../utils/dates';
import { huLocale, deLocale } from '../../config/datepicker-locales';
import CustomInputField from './CustomInputField';
import DatePickerFooter from './DatePickerFooter';

const EN = 'en';
const DE = 'de';

const CALENDAR_VIEWPORT_MARGIN = 8;

// The library positions the popper relative to the input only ("top" ignores
// the viewport entirely), so on short screens the calendar header can end up
// above the visible area (CXR-2098). Shift the opened popper back into view,
// prioritizing the top edge so the month navigation always stays reachable.
const keepCalendarInViewport = wrapper => {
  const container = wrapper.querySelector('.DatePicker__calendarContainer');

  if (!container) {
    return;
  }

  const { top, bottom } = container.getBoundingClientRect();
  const viewportHeight = document.documentElement.clientHeight;
  let shift = 0;

  if (bottom > viewportHeight - CALENDAR_VIEWPORT_MARGIN) {
    shift = viewportHeight - CALENDAR_VIEWPORT_MARGIN - bottom;
  }

  if (top + shift < CALENDAR_VIEWPORT_MARGIN) {
    shift = CALENDAR_VIEWPORT_MARGIN - top;
  }

  if (shift !== 0) {
    container.style.top = `${container.offsetTop + shift}px`;
    container.style.bottom = 'auto';
  }
};

const useStyles = makeStyles({
  calendarWrapper: {
    width: '100%'
  }
}, { name: 'CustomDatePicker' });

/**
 * @param {Object} props - Component props, any additional props passed to the DatePicker
 * @param {function(Date): void} onChange - Callback to handle selected date changes
 * @param {string} value - The currently selected date
 * @param {string} dateFormat - Optional custom date format (not currently used directly)
 * @param {string} inputLabel - Label text displayed above the input field
 * @param {boolean} error - If true, the label will be displayed in an error state for InputField
 * @param {string} errorMessage - Error message shown below the input
 *
 * @returns {JSX.Element}
 */
const CustomDatePicker = ({
  onChange,
  value,
  dateFormat,
  inputLabel,
  error,
  errorMessage,
  ...props }) => {
  const classes = useStyles();
  const {  i18n } = useTranslation();
  const [ inputValue, setInputValue ] = useState(null);
  const wrapperRef = useRef(null);
  const minDate = new Date();

  useEffect(() => {
    if (value) {
      setInputValue(convertDateToModernCalendarFormat(new Date(value)));
    }
  }, [ value ]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    // rAF: wait for the library's own layout effect to apply the "-top" class
    // before measuring, but still reposition before the browser paints
    const reposition = () => window.requestAnimationFrame(
      () => keepCalendarInViewport(wrapper)
    );
    const observer = new MutationObserver(reposition);

    observer.observe(wrapper, { childList: true, subtree: true });
    window.addEventListener('resize', reposition);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, []);

  const handleMonthChange = month => {
    const newDate = addMonthsToDate(new Date(formatDateString(inputValue)),month);

    setInputValue(convertDateToModernCalendarFormat(newDate));
    onChange(newDate);
  };

  return (
    <div ref={ wrapperRef }>
      <DatePicker
        wrapperClassName={ classes.calendarWrapper }
        value={ inputValue }
        shouldHighlightWeekends
        minimumDate={ convertDateToModernCalendarFormat(minDate) }
        colorPrimary="#99B64E"
        onChange={ date => onChange(new Date(formatDateString(date))) }
        renderInput={ ({ ref }) => (
          <CustomInputField
            ref={ ref }
            inputValue={ inputValue }
            inputLabel={ inputLabel }
            error={ error }
            errorMessage={ errorMessage }
          />
        ) }
        renderFooter={ () => (
          < DatePickerFooter
            handleMonthChange={ handleMonthChange }
            onChange={ onChange }
          />
        ) }
        locale={ i18n.language === EN ? EN : i18n.language === DE ? deLocale : huLocale }
        calendarPopperPosition="top" //to prevent scrolling issues, it will be always visible
        { ...props }
      />
    </div>
  );
};

export default CustomDatePicker;
